"use client";

import { useEffect, useRef } from "react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import type { Tournament } from "@/entities/tournament/model/types";
import { translateUi } from "@/shared/lib/ui-translate";

const STORAGE_KEY = "tournament-deadline-toast";
const MAX_HOURS_TO_SHOW = 24;
const REGISTRATION_CLOSES_HOURS_BEFORE = 1;

function formatTimeLeft(ms: number): string {
  if (ms <= 0) return translateUi("litUseTournamentDeadlineToast.nMin", { minutes: 0 });
  const totalMinutes = Math.floor(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return translateUi("litUseTournamentDeadlineToast.nChNMin", { hours, minutes });
  return translateUi("litUseTournamentDeadlineToast.nMin", { minutes });
}

export function useTournamentDeadlineToast(
  tournamentId: string | undefined,
  tournament: Tournament | null | undefined,
  myRegistration: { registrationStatus?: string | null } | null | undefined,
) {
  const t = useTranslations("Tournaments.overview");
  const shownRef = useRef(false);

  useEffect(() => {
    if (!tournamentId || !tournament?.startDate || shownRef.current) return;
    const status = (myRegistration?.registrationStatus ?? "").toString().toLowerCase();
    if (status !== "approved" && status !== "pending") return;

    const startDate = new Date(tournament.startDate);
    if (isNaN(startDate.getTime())) return;
    const now = new Date();
    if (now >= startDate) return;

    const registrationDeadline = new Date(startDate);
    registrationDeadline.setTime(
      registrationDeadline.getTime() - REGISTRATION_CLOSES_HOURS_BEFORE * 60 * 60 * 1000,
    );
    const tournamentStatus = String(tournament.status ?? tournament.backendStatus ?? "").toLowerCase();
    const isRegistrationOpen = tournamentStatus === "registrationopen";

    let message: string;
    let timeLeftMs: number;
    if (isRegistrationOpen && now < registrationDeadline) {
      timeLeftMs = registrationDeadline.getTime() - now.getTime();
      if (timeLeftMs > MAX_HOURS_TO_SHOW * 60 * 60 * 1000) return;
      message = t("toastRegistrationEndsIn", { time: formatTimeLeft(timeLeftMs) });
    } else {
      timeLeftMs = startDate.getTime() - now.getTime();
      if (timeLeftMs > MAX_HOURS_TO_SHOW * 60 * 60 * 1000) return;
      message = t("toastTournamentStartsIn", { time: formatTimeLeft(timeLeftMs) });
    }

    try {
      const key = `${STORAGE_KEY}:${tournamentId}`;
      if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch {
      return;
    }
    shownRef.current = true;
    toast.info(message, { duration: 8000 });
  }, [tournamentId, tournament, myRegistration, t]);
}
